/**
 * Feedback Filters Component
 * Filter interface for the feedback dashboard
 */

import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import { FeedbackFilter, FeedbackType } from '../../../types/feedback';

interface FeedbackFiltersProps {
  filters: FeedbackFilter;
  onChange: (filters: FeedbackFilter) => void;
  feedbackTypes: FeedbackType[];
}

const FeedbackFilters: React.FC<FeedbackFiltersProps> = ({
  filters,
  onChange,
  feedbackTypes
}) => {
  const [dateRange, setDateRange] = useState({
    start: filters.dateRange?.start?.toISOString().slice(0, 10) || '',
    end: filters.dateRange?.end?.toISOString().slice(0, 10) || ''
  });

  const feedbackTypeLabels: Record<FeedbackType, string> = {
    'quick-reaction': 'Quick Reactions',
    'page-comment': 'Page Comments',
    'element-feedback': 'Element Feedback',
    'flow-rating': 'Flow Ratings',
    'bug-report': 'Bug Reports',
    'feature-request': 'Feature Requests',
    'general': 'General Feedback'
  };

  const handleTypeToggle = (type: FeedbackType) => {
    const currentTypes = filters.type || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];
    
    onChange({
      ...filters,
      type: newTypes.length > 0 ? newTypes : undefined
    });
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    const newDateRange = { ...dateRange, [field]: value };
    setDateRange(newDateRange);

    if (newDateRange.start && newDateRange.end) {
      onChange({
        ...filters,
        dateRange: {
          start: new Date(newDateRange.start),
          end: new Date(newDateRange.end)
        }
      });
    } else if (!newDateRange.start && !newDateRange.end) {
      onChange({
        ...filters,
        dateRange: undefined
      });
    }
  };

  const handlePageUrlChange = (pageUrl: string) => {
    onChange({
      ...filters,
      pageUrl: pageUrl.trim() || undefined
    });
  };

  const clearFilters = () => {
    setDateRange({ start: '', end: '' });
    onChange({});
  };

  const hasActiveFilters = !!(
    filters.type?.length ||
    filters.dateRange ||
    filters.pageUrl
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-800">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-terracotta hover:text-terracotta-dark transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Feedback Type Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          Feedback Type
        </label>
        <div className="space-y-2">
          {feedbackTypes.map(type => (
            <label key={type} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={filters.type?.includes(type) || false}
                onChange={() => handleTypeToggle(type)}
                className="w-4 h-4 text-terracotta border-stone-300 rounded focus:ring-terracotta"
              />
              <span className="text-slate-700">
                {feedbackTypeLabels[type]}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-slate-700">
          Date Range
        </label>
        <div className="space-y-2">
          <div>
            <label htmlFor="date-start" className="block text-xs text-slate-600 mb-1">
              From
            </label>
            <input
              id="date-start"
              type="date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta"
            />
          </div>
          <div>
            <label htmlFor="date-end" className="block text-xs text-slate-600 mb-1">
              To
            </label>
            <input
              id="date-end"
              type="date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg 
                         focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta"
            />
          </div>
        </div>
      </div>

      {/* Page URL Filter */}
      <div className="space-y-3">
        <label htmlFor="page-url" className="block text-sm font-medium text-slate-700">
          Page URL
        </label>
        <input
          id="page-url"
          type="text"
          value={filters.pageUrl || ''}
          onChange={(e) => handlePageUrlChange(e.target.value)}
          placeholder="Filter by page path..."
          className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg 
                     focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-terracotta"
        />
        <p className="text-xs text-slate-500">
          Enter part of the URL path to filter feedback
        </p>
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="pt-4 border-t border-stone-200">
          <h4 className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wide">
            Active Filters
          </h4>
          <div className="space-y-1 text-xs">
            {filters.type && filters.type.length > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Types:</span>
                <span className="text-slate-700">{filters.type.length} selected</span>
              </div>
            )}
            {filters.dateRange && (
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Date:</span>
                <span className="text-slate-700">
                  {filters.dateRange.start.toLocaleDateString()} - {filters.dateRange.end.toLocaleDateString()}
                </span>
              </div>
            )}
            {filters.pageUrl && (
              <div className="flex items-center gap-1">
                <span className="text-slate-500">URL:</span>
                <span className="text-slate-700 truncate max-w-32">
                  {filters.pageUrl}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackFilters;